import React, { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import Select from 'react-select';
import * as XLSX from 'xlsx';

const SchemeWiseStatistics = () => {
  const pid = useSelector((state) => state.user.projectId);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [schemeTypeOptions, setSchemeTypeOptions] = useState([]);
  const [selectedSchemeTypes, setSelectedSchemeTypes] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedState, setSelectedState] = useState(null);
  const [stateOptions, setStateOptions] = useState([]);
  
  const [infoPopup, setInfoPopup] = useState({ show: false, title: '', description: '', example: '' });
  
  const [appliedFilters, setAppliedFilters] = useState({
    selectedSchemeTypes: [],
    startDate: '',
    endDate: '',
    selectedState: null
  });

  const SCHEME_TYPE_MAPPING = {
    'sch': 'Schemes',
    'doc': 'Document',
    'dfl': 'DFL',
    'yc': 'Yojana Card'
  };

  const definitions = {
    "Scheme wise statistics": {
      title: "Scheme wise statistics",
      description: "This view provides a detailed breakdown of application statistics across different schemes. You can analyze performance of individual schemes to identify which schemes are performing well and which need attention."
    },
    "Date Range": {
      title: "Date Range",
      description: "These dates will be used to report on the work done between these dates. Both the start date and end date are inclusive in the query.",
      example: "If you select 1/Jan/2026 - 31/Mar/2026, the data will include work done on 1st January AND 31st March as well."
    },
    "Service Type": {
      title: "Service Type",
      description: "Filter data by service type to analyze specific service categories.",
      example: "Example: Schemes, Document, DFL, Yojana Card, EDP"
    }
  };

  const InfoButton = ({ title }) => {
    const definition = definitions[title];
    if (!definition) return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setInfoPopup({
            show: true,
            title: definition.title,
            description: definition.description,
            example: definition.example || ''
          });
        }}
        className="group relative ml-1.5 inline-flex items-center justify-center w-4 h-4 flex-shrink-0 rounded-full text-gray-400 transition-colors duration-300 ease-out hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-400"
        aria-label={`Info about ${title}`}
      >
        <span className="absolute inset-0 -m-2 rounded-full bg-white/0 ring-1 ring-transparent transition-all duration-300 ease-out group-hover:bg-white/70 group-hover:backdrop-blur-md group-hover:ring-black/5 group-hover:shadow-[0_4px_14px_rgba(99,102,241,0.18)]" />

        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="relative w-3.5 h-3.5 transition-transform duration-300 ease-out group-hover:scale-110"
        >
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 16v-4.5" />
          <path d="M12 7.8h.01" />
        </svg>
      </button>
    );
  };

  const InfoPopup = () => {
    if (!infoPopup.show) return null;
    
    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]"
        onClick={closeInfoPopup}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden animate-scaleIn"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 18a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">{infoPopup.title}</h3>
            </div>
            <button
              onClick={closeInfoPopup}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 leading-relaxed">{infoPopup.description}</p>
            </div>

            {infoPopup.example && (
              <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📊</span>
                  <p className="text-sm font-semibold text-gray-800">Example</p>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line font-mono bg-white/50 p-3 rounded-lg border border-blue-100">
                  {infoPopup.example}
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeInfoPopup}
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const closeInfoPopup = () => {
    setInfoPopup({ show: false, title: '', description: '', example: '' });
  };

  useEffect(() => {
    const fetchSchemeTypes = async () => {
      try {
        const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-scheme-type/?pid=${pid}`);
        const data = await response.json();
        
        setSchemeTypeOptions(data.unique_Scheme_Types.map(type => {
          const fullForm = SCHEME_TYPE_MAPPING[type] || type;
          return { 
            value: type, 
            label: `${type} - ${fullForm}`,
            abbreviation: type,
            fullForm: fullForm
          };
        }));
      } catch (err) {
        console.error('Error fetching scheme types:', err);
      }
    };
    fetchSchemeTypes();
  }, [pid]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        let url = `https://api-project360-insights.haqdarshak.com/kpi/unique-states/?pid=${pid}`;
        
        if (selectedSchemeTypes.length > 0) {
          selectedSchemeTypes.forEach(type => {
            url += `&SchemeType=${type.abbreviation}`;
          });
        }
        
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch states');
        const result = await response.json();
        
        setStateOptions(result.states || []);
      } catch (err) {
        console.error('Error fetching states:', err);
        setStateOptions([]);
      }
    };
    
    fetchStates();
  }, [pid, selectedSchemeTypes]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        let url = `https://api-project360-insights.haqdarshak.com/kpi/state-wise/?pid=${pid}&view_by=Scheme`;
        
        if (appliedFilters.selectedSchemeTypes.length > 0) {
          appliedFilters.selectedSchemeTypes.forEach(type => {
            url += `&SchemeType=${type.abbreviation}`;
          });
        }
        
        if (appliedFilters.selectedState) {
          url += `&state=${appliedFilters.selectedState}`;
        }
        
        if (appliedFilters.startDate) {
          url += `&start_date=${appliedFilters.startDate}`;
        }
        if (appliedFilters.endDate) {
          url += `&end_date=${appliedFilters.endDate}`;
        }

        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch data');
        const result = await response.json();
        
        setData(result);
        setError(null);
        setCurrentPage(0);
      } catch (err) {
        setError(err.message);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [pid, appliedFilters]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      selectedSchemeTypes: [...selectedSchemeTypes],
      startDate: startDate,
      endDate: endDate,
      selectedState: selectedState
    });
  };

  const handleClearFilters = () => {
    setSelectedSchemeTypes([]);
    setStartDate('');
    setEndDate('');
    setSelectedState(null);
    setAppliedFilters({
      selectedSchemeTypes: [],
      startDate: '',
      endDate: '',
      selectedState: null
    });
  };

  const handleExport = () => {
    const exportData = filteredData.map(item => ({
      'Scheme Name': item.SchemeName || item.State,
      'State': item.State || '',
      'Applications Opened': item.application_open || 0,
      'Applications Submitted': item.docket_submitted || 0,
      'Applications with Benefit Received': item.br_count || 0,
      'Total Cases': item.TotalCases || 0,
      'Rejected Count': item.rejected_count || 0
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Scheme_Statistics');
    XLSX.writeFile(workbook, `Scheme_Statistics_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const filteredData = data.filter(item => item.SchemeName || item.State);
  
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const handlePrev = () => setCurrentPage(prev => Math.max(prev - 1, 0));
  const handleNext = () => setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow border-2 border-gray-300 max-w-[92vw] ml-[3vw] p-4 mb-10">
      <div className="flex items-center mb-6">
        <h2 className="text-xl font-bold text-pink-900">
          Scheme wise statistics
        </h2>
        <InfoButton title="Scheme wise statistics" />
      </div>

      {appliedFilters.selectedState && (
        <p className="text-lg font-normal text-gray-600 ml-2 -mt-4 mb-4">
          Showing schemes in: {appliedFilters.selectedState}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filter by State
          </label>
          <Select
            options={stateOptions.map(state => ({ value: state, label: state }))}
            value={selectedState ? { value: selectedState, label: selectedState } : null}
            onChange={(option) => setSelectedState(option ? option.value : null)}
            className="basic-single-select"
            classNamePrefix="select"
            placeholder="Select state..."
            isClearable
            isSearchable
            isDisabled={stateOptions.length === 0}
          />
        </div>

        <div>
          <div className="flex items-center mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Filter by Service Type
            </label>
            <InfoButton title="Service Type" />
          </div>
          <Select
            isMulti
            options={schemeTypeOptions}
            value={selectedSchemeTypes}
            onChange={setSelectedSchemeTypes}
            className="basic-multi-select"
            classNamePrefix="select"
            placeholder="Select service type..."
            isClearable
            isSearchable
            formatOptionLabel={(option) => (
              <div>
                <span className="text-gray-800 ml-1">{option.fullForm}</span>
              </div>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-2 lg:col-span-2">
          <div>
            <div className="flex items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Start Date
              </label>
              <InfoButton title="Date Range" />
            </div>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <div className="flex items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">
                End Date
              </label>
            </div>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-3 mb-6">
        <button
          onClick={handleApplyFilters}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium shadow-sm"
        >
          Apply Filters
        </button>
        <button
          onClick={handleClearFilters}
          className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition font-medium"
        >
          Clear Filters
        </button>
        <button
          onClick={handleExport}
          className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition font-medium shadow-sm ml-auto"
          disabled={filteredData.length === 0}
        >
          Export to Excel
        </button>
      </div>

      {filteredData.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500 text-lg">No scheme data available</p>
          <p className="text-gray-400 text-sm mt-2">
            Apply filters to view scheme-wise data
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
            <div className="flex items-center gap-2">
              <label htmlFor="items-per-page" className="text-sm font-medium text-gray-700">
                Items Per Page:
              </label>
              <select
                id="items-per-page"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(0);
                }}
                className="px-3 py-1 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                {[1, 2, 3, 5, 10, 20, 50, 100, 200].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            <div className="text-sm text-purple-700">
              Showing {paginatedData.length} of {filteredData.length} schemes
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrev}
                disabled={currentPage === 0}
                className={`px-4 py-2 rounded-md text-sm transition ${
                  currentPage === 0
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                Previous
              </button>

              <span className="text-sm px-4 py-2 bg-gray-100 rounded">
                Page {currentPage + 1} of {totalPages || 1}
              </span>

              <button
                onClick={handleNext}
                disabled={currentPage >= totalPages - 1 || totalPages === 0}
                className={`px-4 py-2 rounded-md text-sm transition ${
                  currentPage >= totalPages - 1 || totalPages === 0
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                Next
              </button>
            </div>
          </div>

          <div className="w-full">
            <div className="overflow-x-auto max-w-[calc(100vw-32px)] lg:max-w-full">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="bg-indigo-50 text-indigo-800 text-sm">
                    <th className="border px-4 py-3 text-left font-semibold">Scheme Name</th>
                    <th className="border px-4 py-3 text-left font-semibold">Applications Opened</th>
                    <th className="border px-4 py-3 text-left font-semibold">Applications Submitted</th>
                    <th className="border px-4 py-3 text-left font-semibold">Applications with Benefit Received</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 text-sm">
                      <td className="border px-4 py-3 font-medium text-gray-700">
                        {item.SchemeName || item.State}
                      </td>

                      <td className="border px-4 py-3">{item.application_open || 0}</td>
                      <td className="border px-4 py-3">{item.docket_submitted || 0}</td>
                      <td className="border px-4 py-3">{item.br_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <InfoPopup />

      <style jsx>{`
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default SchemeWiseStatistics;